import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { axiosService } from 'shared/helpers/axios';

const FORMAT_LABEL = { numeric: 'Numbered', 'author-date': 'Author–date', author: 'Author' };

/**
 * Search the ~10,000 journal styles of the Zotero Style Repository (via the backend).
 *
 * value: { title, styleId } — styleId is null for a journal typed freely.
 * onChange({ title, styleId, styleTitle, format }) — styleId null when typed freely
 * (allowFree) or cleared.
 */
const JournalPicker = ({ value, onChange, label = 'Target journal', helperText, allowFree = true, size, autoFocus }) => {
  const [input, setInput] = useState(value?.title || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setInput(value?.title || ''); }, [value?.title]);

  useEffect(() => {
    const q = input.trim();
    // No search once a listed journal is selected and its title is shown
    if (q.length < 2 || (value?.styleId && q === value.title)) { setOptions([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      axiosService.get('/proposal/journal-styles/', { params: { q } })
        .then((res) => { if (!cancelled) { setOptions(res.data || []); setError(null); } })
        .catch(() => { if (!cancelled) setError('Journal search is unavailable right now.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [input, value?.title, value?.styleId]);

  // Stable identity: MUI resets the typed text whenever `value` changes
  const selected = useMemo(
    () => (value?.styleId ? { id: value.styleId, title: value.title } : null),
    [value?.styleId, value?.title],
  );

  return (
    <Autocomplete
      freeSolo={allowFree}
      size={size}
      options={options}
      value={selected}
      inputValue={input}
      filterOptions={(x) => x}
      loading={loading}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.title)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onInputChange={(e, text, reason) => {
        setInput(text);
        // Free-typed journal: keep the title, no repository style
        if (allowFree && reason === 'input') onChange({ title: text, styleId: null, styleTitle: '', format: null });
      }}
      onChange={(e, option) => {
        if (!option) { onChange({ title: '', styleId: null, styleTitle: '', format: null }); return; }
        if (typeof option === 'string') { onChange({ title: option, styleId: null, styleTitle: '', format: null }); return; }
        onChange({ title: option.title, styleId: option.id, styleTitle: option.title, format: option.format });
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="body2" sx={{ flex: 1 }}>{option.title}</Typography>
            <Chip size="small" variant="outlined" label={FORMAT_LABEL[option.format] || option.format} />
          </Box>
        </li>
      )}
      slotProps={{ popper: { sx: { zIndex: 10001 } } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoFocus={autoFocus}
          placeholder="Start typing a journal name… (Optional)"
          error={Boolean(error)}
          helperText={error || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default JournalPicker;
